import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// photo.url may be absolute (https://...) or relative (/api/photos/file/:id)
const photoSrc = (url) => (url && url.startsWith('http')) ? url : `${API_URL}${url}`;

const GalleryPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [mobile, setMobile] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const touchStartX = useRef(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');

  // Selfie update state
  const [selfieMode, setSelfieMode] = useState(false);      // panel open
  const [newSelfieBlob, setNewSelfieBlob] = useState(null);
  const [newSelfiePreview, setNewSelfiePreview] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [cameraError, setCameraError] = useState('');
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [updateMsg, setUpdateMsg] = useState('');

  const videoRef    = useRef(null);
  const streamRef   = useRef(null);
  const fileInputRef = useRef(null);

  // ── Camera ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!cameraActive || !streamRef.current) return;
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = streamRef.current;
    const onPlaying = () => setVideoReady(true);
    video.addEventListener('playing', onPlaying);
    const t = setInterval(() => {
      if (video.videoWidth > 0) { setVideoReady(true); clearInterval(t); }
    }, 200);
    return () => { video.removeEventListener('playing', onPlaying); clearInterval(t); };
  }, [cameraActive]);

  const startCamera = useCallback(async (facing) => {
    setCameraError('');
    setVideoReady(false);
    const mode = facing || 'user';
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 720 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      setFacingMode(mode);
      setCameraActive(true);
    } catch {
      setCameraError('Camera access denied. Please allow camera access and try again.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setVideoReady(false);
    setCameraActive(false);
  }, []);

  const flipCamera = useCallback(async () => {
    const next = facingMode === 'user' ? 'environment' : 'user';
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setVideoReady(false);
    setCameraActive(false);
    setTimeout(() => startCamera(next), 100);
  }, [facingMode, startCamera]);

  const captureSelfie = useCallback(() => {
    if (!videoRef.current) return;
    const { videoWidth, videoHeight } = videoRef.current;
    if (!videoWidth || !videoHeight) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoWidth; canvas.height = videoHeight;
    const ctx = canvas.getContext('2d');
    if (facingMode === 'user') { ctx.translate(canvas.width, 0); ctx.scale(-1, 1); }
    ctx.drawImage(videoRef.current, 0, 0);
    canvas.toBlob(blob => {
      setNewSelfieBlob(blob);
      setNewSelfiePreview(URL.createObjectURL(blob));
      stopCamera();
    }, 'image/jpeg', 0.9);
  }, [stopCamera, facingMode]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setNewSelfieBlob(file);
    setNewSelfiePreview(URL.createObjectURL(file));
    stopCamera();
    e.target.value = '';
  };

  const closeSelfiePanel = () => {
    setSelfieMode(false);
    setNewSelfieBlob(null);
    setNewSelfiePreview(null);
    setUpdateMsg('');
    setCameraError('');
    stopCamera();
  };

  const handleSelfieUpdate = async () => {
    if (!newSelfieBlob || !result) return;
    setUpdateSubmitting(true);
    setUpdateMsg('');
    try {
      const formData = new FormData();
      formData.append('selfie', newSelfieBlob, 'selfie.jpg');
      // Use gallery token endpoint if available, otherwise fall back to register endpoint
      if (token) {
        await axios.put(`${API_URL}/api/gallery/token/${token}/selfie`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        formData.append('event_id', result.event?.id);
        formData.append('mobile_number', result.visitor?.mobile);
        await axios.post(`${API_URL}/api/register/update-selfie`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      closeSelfiePanel();
      setUpdateMsg('Selfie updated! Re-scanning your photos...');
      // Poll every 5s for new results
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const res = await axios.get(`${API_URL}/api/gallery/token/${token}`);
          if (res.data.status !== 'pending' && res.data.status !== 'selfie_error' || attempts >= 12) {
            setResult(res.data);
            setUpdateMsg('');
            clearInterval(poll);
          }
        } catch { clearInterval(poll); setUpdateMsg(''); }
      }, 5000);
    } catch (err) {
      setUpdateMsg(err.response?.data?.error || 'Update failed. Please try again.');
    } finally {
      setUpdateSubmitting(false);
    }
  };

  // ── Gallery load ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    let refreshTimer = null;
    let selfieErrorAttempts = 0;
    const SELFIE_ERROR_MAX_POLLS = 24; // poll up to 2 minutes (24 × 5s) while worker computes embedding
    const load = () => {
      setLoading(true);
      axios.get(`${API_URL}/api/gallery/token/${token}`)
        .then(res => {
          setResult(res.data);
          if (res.data.status === 'pending') {
            refreshTimer = setTimeout(load, 5000);
          } else if (res.data.status === 'selfie_error') {
            // Open selfie panel immediately so visitor isn't looking at a blank page
            setSelfieMode(true);
            selfieErrorAttempts++;
            if (selfieErrorAttempts < SELFIE_ERROR_MAX_POLLS) {
              // Keep polling silently — if worker finishes, page will auto-update
              refreshTimer = setTimeout(load, 5000);
            }
          }
        })
        .catch(err => setError(err.response?.data?.error || 'Could not load your gallery. The link may be invalid.'))
        .finally(() => setLoading(false));
    };
    load();
    return () => { if (refreshTimer) clearTimeout(refreshTimer); };
  }, [token]);

  const photos  = result?.photos ?? [];
  const total   = result?.total  ?? photos.length;
  const currentPhoto = lightboxIndex !== null ? photos[lightboxIndex] : null;

  const openLightbox  = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const goPrev = useCallback(() => setLightboxIndex(i => (i > 0 ? i - 1 : photos.length - 1)), [photos.length]);
  const goNext = useCallback(() => setLightboxIndex(i => (i < photos.length - 1 ? i + 1 : 0)), [photos.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape')     closeLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, goPrev, goNext]);

  useEffect(() => {
    if (!selfieMode) return;
    const onKey = (e) => { if (e.key === 'Escape') closeSelfiePanel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selfieMode]);

  const onLightboxTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onLightboxTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) { delta < 0 ? goNext() : goPrev(); }
    touchStartX.current = null;
  };

  const handleShare = async () => {
    if (!currentPhoto) return;
    const photoUrl = photoSrc(currentPhoto.url).startsWith('http')
      ? photoSrc(currentPhoto.url)
      : `${window.location.origin}${photoSrc(currentPhoto.url)}`;
    if (navigator.share) {
      try { await navigator.share({ title: currentPhoto.file_name, url: photoUrl }); } catch {}
    } else {
      await navigator.clipboard.writeText(photoUrl);
      alert('Photo link copied!');
    }
  };

  const handleSearch = async () => {
    if (!/^\d{10}$/.test(mobile)) { setError('Please enter a valid 10-digit mobile number.'); return; }
    setError(''); setLoading(true); setResult(null);
    try {
      const res = await axios.get(`${API_URL}/api/gallery/${mobile}`);
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 202) setResult({ pending: true, ...err.response.data });
      else setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  const handleRefresh = async () => {
    if (!token || refreshing) return;
    setRefreshing(true); setRefreshMsg('');
    try {
      await axios.post(`${API_URL}/api/gallery/token/${token}/refresh`);
      setRefreshMsg('Scanning for new photos...');
      setTimeout(async () => {
        try {
          const res = await axios.get(`${API_URL}/api/gallery/token/${token}`);
          setResult(res.data); setRefreshMsg('');
        } catch { setRefreshMsg(''); }
        finally { setRefreshing(false); }
      }, 8000);
    } catch { setRefreshMsg('Could not refresh. Please try again.'); setRefreshing(false); }
  };

  const downloadPhoto = (url, filename) => {
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
  };

  const visitorName = result?.visitor?.name || result?.visitor_name || result?.name || 'there';

  return (
    <div style={styles.page}>
      {/* Lightbox */}
      {currentPhoto && (
        <div
          style={styles.lightboxOverlay}
          onClick={closeLightbox}
          onTouchStart={onLightboxTouchStart}
          onTouchEnd={onLightboxTouchEnd}
        >
          <div style={styles.lightboxContent} onClick={e => e.stopPropagation()}>
            {/* Header: counter + close */}
            <div style={styles.lightboxHeader}>
              <span style={styles.lightboxCounter}>{lightboxIndex + 1} / {photos.length}</span>
              <button style={styles.closeBtn} onClick={closeLightbox}>✕</button>
            </div>

            {/* Photo — full-width, share button overlaid bottom-right */}
            <div style={styles.lightboxImgWrap}>
              <img src={photoSrc(currentPhoto.url)} alt="Event photo" style={styles.lightboxImg} />
              <button style={styles.shareBtn} onClick={handleShare}>Share</button>
            </div>

            {/* Nav + download below photo */}
            <div style={styles.lightboxControls}>
              {photos.length > 1 && (
                <button style={styles.lbNavBtn} onClick={goPrev}>&#8249; Prev</button>
              )}
              <button style={styles.downloadBtn} onClick={() => downloadPhoto(photoSrc(currentPhoto.url), currentPhoto.file_name)}>
                ⬇ Download
              </button>
              {photos.length > 1 && (
                <button style={styles.lbNavBtn} onClick={goNext}>Next &#8250;</button>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={styles.container}>
        {/* Single hidden file input, always mounted */}
        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileUpload} />

        {/* Hero — hidden in selfie mode to give panel full screen */}
        {!selfieMode && (
          <div style={styles.hero}>
            <div style={styles.heroIcon}>📷</div>
            {result?.event?.name ? (
              <>
                <h1 style={styles.heroTitle}>{result.event.name}</h1>
                <div style={styles.heroMeta}>
                  {result.event.date && (
                    <span style={styles.heroMetaItem}>
                      {new Date(result.event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  )}
                  {result.event.venue && (
                    <span style={styles.heroMetaItem}>{result.event.venue}</span>
                  )}
                </div>
              </>
            ) : (
              <>
                <h1 style={styles.heroTitle}>Your Event Photos</h1>
                <p style={styles.heroSub}>Enter your registered mobile number to view your personalized photo album</p>
              </>
            )}
          </div>
        )}

        {/* Search Box — hidden when using token link */}
        <div style={{ ...styles.searchCard, ...(token ? { display: 'none' } : {}) }}>
          <div style={styles.searchRow}>
            <input
              style={styles.searchInput}
              placeholder="Enter 10-digit mobile number"
              value={mobile}
              onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              inputMode="numeric"
              maxLength={10}
            />
            <button
              style={{ ...styles.searchBtn, ...(loading ? styles.searchBtnDisabled : {}) }}
              onClick={handleSearch} disabled={loading}
            >
              {loading ? '...' : 'View'}
            </button>
          </div>
          {error && <p style={styles.errorMsg}>{error}</p>}
        </div>


        {/* Pending State */}
        {(result?.pending || result?.status === 'pending') && (
          <div style={styles.statusCard}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚙️</div>
            <h3 style={{ margin: '0 0 0.5rem', color: '#1a1a2e' }}>Hello, {result.visitor?.name || result.visitor_name}!</h3>
            <p style={{ margin: 0, color: '#666' }}>{result.message || 'Your photos are being processed. Please wait...'}</p>
            <p style={{ margin: '0.5rem 0 0', color: '#999', fontSize: '0.85rem' }}>Event: {result.event?.name || result.event_name}</p>
            <p style={{ margin: '0.75rem 0 0', color: '#a5b4fc', fontSize: '0.8rem' }}>Auto-refreshing...</p>
          </div>
        )}

        {/* Selfie update panel */}
        {selfieMode && (
          <div style={styles.selfiePanel}>
            {/* Personalised greeting — hidden while camera is live to save space */}
            {!cameraActive && !newSelfiePreview && (
              <div style={styles.selfiePanelGreeting}>
                <div style={styles.selfiePanelAvatar}>🙏</div>
                <div>
                  <p style={styles.selfiePanelWelcome}>
                    Welcome, {result?.visitor?.name || 'Guest'}!
                  </p>
                  <p style={styles.selfiePanelEventName}>
                    {result?.event?.name || 'Event Photos'}
                  </p>
                  <p style={styles.selfiePanelHint}>
                    To find your photos from the event, we need a clear selfie of you.
                    Please upload a photo or take one right now — it only takes a second!
                  </p>
                </div>
              </div>
            )}

            {/* Preview greeting */}
            {newSelfiePreview && !cameraActive && (
              <p style={styles.selfiePanelTitle}>
                Looking good, {result?.visitor?.name?.split(' ')[0] || 'there'}! 👍
              </p>
            )}

            {cameraError && <p style={styles.cameraErrorMsg}>{cameraError}</p>}

            {/* Choice buttons */}
            {!cameraActive && !newSelfiePreview && (
              <div style={styles.selfieChoiceRow}>
                <button style={styles.selfieChoiceBtn} onClick={() => fileInputRef.current?.click()}>
                  📁 Upload from Gallery
                </button>
                <button style={{ ...styles.selfieChoiceBtn, background: '#4f46e5' }} onClick={() => startCamera('user')}>
                  📷 Take a Selfie
                </button>
              </div>
            )}

            {/* Camera view — full width, maximum height */}
            {cameraActive && (
              <div style={styles.cameraWrap}>
                <video
                  ref={videoRef} autoPlay playsInline muted
                  style={{ ...styles.cameraVideo, transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                />
                {!videoReady && <p style={styles.cameraStarting}>Starting camera…</p>}
                <div style={styles.cameraControls}>
                  <button style={styles.camBtn} onClick={stopCamera}>Cancel</button>
                  <button style={{ ...styles.camBtn, ...styles.camBtnCapture, ...(videoReady ? {} : { opacity: 0.45 }) }}
                    onClick={captureSelfie} disabled={!videoReady}>
                    Capture
                  </button>
                  <button style={styles.camBtn} onClick={flipCamera}>🔄 Flip</button>
                </div>
              </div>
            )}

            {/* Preview after capture/upload */}
            {newSelfiePreview && (
              <div style={styles.previewWrap}>
                <img src={newSelfiePreview} alt="Selfie preview" style={styles.previewImg} />
                <div style={styles.previewActions}>
                  <button style={{ ...styles.selfieChoiceBtn, background: '#6b7280' }}
                    onClick={() => { setNewSelfieBlob(null); setNewSelfiePreview(null); }}>
                    Retake
                  </button>
                  <button
                    style={{ ...styles.selfieChoiceBtn, background: '#4f46e5', ...(updateSubmitting ? { opacity: 0.6 } : {}) }}
                    onClick={handleSelfieUpdate} disabled={updateSubmitting}>
                    {updateSubmitting ? 'Saving…' : 'Save & Find My Photos'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {updateMsg && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.75rem', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#1d4ed8', fontSize: '0.875rem' }}>
            {updateMsg}
          </div>
        )}

        {/* Results */}
        {result && !result.pending && result.status !== 'pending' && result.status !== 'selfie_error' && (
          <>
            {/* Selfie + visitor header */}
            <div style={styles.visitorHeader}>
              {/* Selfie card */}
              <div style={styles.selfieCard}>
                {result.selfie_url ? (
                  <img src={`${API_URL}${result.selfie_url}`} alt="Your selfie" style={styles.selfieThumb} loading="lazy" />
                ) : (
                  <div style={styles.selfieThumbEmpty}>👤</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={styles.selfieLabel}>Your registered selfie</p>
                  <p style={styles.selfieHint}>Update it for better photo matching accuracy</p>
                  {token && (
                    <button style={styles.updateSelfieBtn} onClick={() => setSelfieMode(true)}>
                      Update Selfie
                    </button>
                  )}
                </div>
              </div>

              {/* Results count + refresh */}
              <div style={styles.resultsHeaderRight}>
                <h2 style={styles.visitorName}>Hello, {visitorName}! 👋</h2>
                <p style={styles.resultsCount}>
                  {total > 0
                    ? `We found ${total} photo${total !== 1 ? 's' : ''} of you`
                    : 'No photos found matching your face yet'}
                </p>
                {token && (
                  <button
                    onClick={handleRefresh} disabled={refreshing}
                    style={{ ...styles.refreshBtn, ...(refreshing ? styles.refreshBtnDisabled : {}) }}
                  >
                    {refreshing ? 'Scanning...' : 'Find more photos'}
                  </button>
                )}
                {refreshMsg && <p style={styles.refreshMsg}>{refreshMsg}</p>}
              </div>
            </div>

            {total === 0 && (
              <div style={styles.emptyState}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
                <p style={{ color: '#888' }}>
                  No photos were matched to your face. Try updating your selfie with a clearer photo, or tap "Find more photos" once the photographer uploads more shots.
                </p>
              </div>
            )}

            <div style={styles.photoGrid}>
              {photos.map((photo, index) => (
                <div key={photo.id} style={styles.photoCard} onClick={() => openLightbox(index)}>
                  <img src={photoSrc(photo.url)} alt="Event photo" style={styles.photo} loading="lazy" />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: { minHeight: '100vh', background: 'linear-gradient(160deg, #0f0c29, #302b63, #24243e)', padding: '0.75rem', fontFamily: "'Segoe UI', system-ui, sans-serif", color: '#fff' },
  container: { maxWidth: '480px', margin: '0 auto' },

  // Hero — compact on mobile
  hero: { textAlign: 'center', padding: '1rem 0 0.75rem' },
  heroIcon: { fontSize: '2rem', marginBottom: '0.4rem' },
  heroTitle: { fontSize: '1.4rem', fontWeight: '800', margin: '0 0 0.4rem', background: 'linear-gradient(90deg, #a78bfa, #60a5fa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  heroSub: { color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', margin: 0 },
  heroMeta: { display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '0.4rem' },
  heroMetaItem: { color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', background: 'rgba(255,255,255,0.08)', borderRadius: '50px', padding: '0.2rem 0.6rem' },

  searchCard: { background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)', borderRadius: '1rem', padding: '1rem', marginBottom: '1rem', border: '1px solid rgba(255,255,255,0.1)' },
  searchRow: { display: 'flex', gap: '0.5rem' },
  searchInput: { flex: 1, padding: '0.75rem 1rem', borderRadius: '0.65rem', border: '1.5px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '1rem', outline: 'none' },
  searchBtn: { padding: '0.75rem 1.25rem', background: 'linear-gradient(135deg, #a78bfa, #60a5fa)', color: '#fff', border: 'none', borderRadius: '0.65rem', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.95rem' },
  searchBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  errorMsg: { color: '#fca5a5', fontSize: '0.82rem', margin: '0.5rem 0 0' },
  statusCard: { background: 'rgba(255,255,255,0.1)', borderRadius: '1.25rem', padding: '2rem 1.25rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' },

  // Visitor header row
  visitorHeader: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '1rem' },
  selfieCard: { display: 'flex', gap: '0.75rem', alignItems: 'center', background: 'rgba(255,255,255,0.07)', borderRadius: '0.75rem', padding: '0.75rem', border: '1px solid rgba(255,255,255,0.1)', flex: '1 1 auto' },
  selfieThumb: { width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', border: '2px solid #a78bfa', flexShrink: 0 },
  selfieThumbEmpty: { width: 52, height: 52, borderRadius: '50%', background: 'rgba(167,139,250,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', flexShrink: 0 },
  selfieLabel: { margin: '0 0 0.15rem', fontWeight: 600, fontSize: '0.8rem', color: '#fff' },
  selfieHint: { margin: '0 0 0.4rem', fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 },
  updateSelfieBtn: { background: 'transparent', border: '1px solid #a78bfa', color: '#a78bfa', borderRadius: '0.4rem', padding: '0.25rem 0.65rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 },
  resultsHeaderRight: { flex: 1, minWidth: '140px' },
  visitorName: { margin: '0 0 0.2rem', fontSize: '1.1rem', fontWeight: '700' },
  resultsCount: { margin: '0 0 0.5rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' },
  refreshBtn: { padding: '0.4rem 0.9rem', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '0.5rem', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' },
  refreshBtnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  refreshMsg: { margin: '0.3rem 0 0', fontSize: '0.75rem', color: '#6366f1' },

  // Selfie panel — fills viewport height, large mobile-first text
  selfiePanel: { background: '#fff', borderRadius: '1rem', padding: '1.25rem', minHeight: 'calc(100vh - 1.5rem)', display: 'flex', flexDirection: 'column' },
  selfiePanelGreeting: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '0.5rem', flex: 1, marginBottom: '1.5rem', padding: '1.25rem 1rem', background: 'linear-gradient(135deg, #f5f3ff, #eff6ff)', borderRadius: '1rem', border: '1px solid #e0e7ff' },
  selfiePanelAvatar: { fontSize: '3rem', lineHeight: 1 },
  selfiePanelWelcome: { margin: 0, fontWeight: 800, color: '#1a1a2e', fontSize: '1.9rem', lineHeight: 1.2 },
  selfiePanelEventName: { margin: 0, fontWeight: 600, color: '#4f46e5', fontSize: '1.1rem' },
  selfiePanelTitle: { margin: '0 0 0.75rem', fontWeight: 700, color: '#1a1a2e', fontSize: '1.25rem' },
  selfiePanelHint: { margin: '0.5rem 0 0', color: '#444', fontSize: '1.05rem', lineHeight: 1.65 },
  cameraErrorMsg: { color: '#dc2626', fontSize: '0.9rem', margin: '0 0 0.75rem' },

  // Choice buttons — full width, tall tap targets
  selfieChoiceRow: { display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: 'auto' },
  selfieChoiceBtn: { width: '100%', padding: '1rem', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '0.75rem', cursor: 'pointer', fontWeight: 700, fontSize: '1.05rem', textAlign: 'center' },

  // Camera — fills full panel width
  cameraWrap: { display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 },
  cameraVideo: { width: '100%', flex: 1, borderRadius: '0.75rem', display: 'block', background: '#000', objectFit: 'cover' },
  cameraStarting: { color: '#888', fontSize: '0.9rem', margin: 0, textAlign: 'center' },
  cameraControls: { display: 'flex', gap: '0.5rem' },
  camBtn: { flex: 1, padding: '0.85rem 0', background: '#374151', color: '#fff', border: 'none', borderRadius: '0.6rem', cursor: 'pointer', fontWeight: 700, fontSize: '1rem' },
  camBtnCapture: { flex: 2, background: '#4f46e5' },

  // Preview
  previewWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', flex: 1 },
  previewImg: { width: '100%', maxWidth: 320, aspectRatio: '1', borderRadius: '1rem', objectFit: 'cover', border: '3px solid #4f46e5' },
  previewActions: { display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', marginTop: 'auto' },

  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.5rem' },
  photoCard: { position: 'relative', borderRadius: '0.75rem', overflow: 'hidden', cursor: 'pointer', aspectRatio: '1', background: '#1a1a2e' },
  photo: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  emptyState: { textAlign: 'center', padding: '2rem 1rem', background: 'rgba(255,255,255,0.05)', borderRadius: '1rem', marginBottom: '1rem' },

  lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  lightboxContent: { width: '100%', maxWidth: 900, display: 'flex', flexDirection: 'column', padding: '0.5rem', maxHeight: '100vh' },
  lightboxHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.25rem 0.25rem 0.5rem' },
  lightboxCounter: { color: '#a78bfa', fontWeight: 700, fontSize: '0.85rem' },
  lightboxImgWrap: { position: 'relative', flex: 1, overflow: 'hidden', borderRadius: '0.75rem', background: '#000' },
  lightboxImg: { width: '100%', maxHeight: 'calc(100vh - 120px)', objectFit: 'contain', display: 'block' },
  shareBtn: { position: 'absolute', bottom: '0.75rem', right: '0.75rem', background: 'rgba(0,0,0,0.65)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '0.5rem', padding: '0.4rem 0.9rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem', backdropFilter: 'blur(6px)' },
  lightboxControls: { display: 'flex', gap: '0.5rem', justifyContent: 'center', alignItems: 'center', padding: '0.5rem 0 0.25rem', flexWrap: 'wrap' },
  lbNavBtn: { background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.45rem 1rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' },
  downloadBtn: { background: '#a78bfa', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.45rem 1rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' },
  closeBtn: { background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.35rem 0.8rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }
};

export default GalleryPage;
